import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Users, UserRound } from 'lucide-react'
import { DashboardShell, DashboardTabs } from '@/components/dashboards'
import type { DashboardTab } from '@/components/dashboards'
import CohortBar from '@/components/analytics/explorer/CohortBar'
import { useCohort } from '@/components/analytics/explorer/explorerState'
import Leaderboard from '@/components/analytics/people/Leaderboard'
import PeoplePicker from '@/components/analytics/people/PeoplePicker'
import PersonProfileView from '@/components/analytics/people/PersonProfileView'
import TeamWorkloadPanel, { useWorkload } from '@/components/analytics/people/TeamWorkload'
import { usePersonProfile } from '@/components/analytics/explorer/analyticsQueries'
import { ROLE_LABEL, type PeopleRole } from '@/services/dashboardAnalytics'

const TABS: DashboardTab[] = [
  { id: 'team', label: 'Team', icon: Users },
  { id: 'person', label: 'Person', icon: UserRound },
]

const ROLES = new Set<PeopleRole>(Object.keys(ROLE_LABEL) as PeopleRole[])

/**
 * People & Team — the desk's people view (T-56, T-58, T-61, T-81).
 *
 * Team: everyone's workload by stage, staffing, and aging, plus leaderboards.
 * Person: pick a role and a name and that person's world opens — headline
 * with peer rank, aging, flow, funnel, relationships, mix, activity and every
 * deal. Role, name and tab live in the URL, so a profile can be sent.
 */
export default function PeopleDashboard() {
  const [params, setParams] = useSearchParams()
  const [cohort, setCohort] = useCohort()
  const rawRole = params.get('role') as PeopleRole | null
  const role: PeopleRole = rawRole && ROLES.has(rawRole) ? rawRole : 'underwriter'
  const name = params.get('name')
  const rawView = params.get('view')
  const view = rawView === 'team' || rawView === 'person' ? rawView : name ? 'person' : 'team'
  // The same cached queries the panels below run — read here only so the
  // shell's Live chip states the age of what is on screen.
  const workload = useWorkload(cohort, 'responsibleParty', view === 'team')
  const profile = usePersonProfile(role, view === 'person' ? name : null)
  const live = view === 'team' ? workload : profile

  const patch = useCallback(
    (next: Record<string, string | null>) =>
      setParams(
        (prev) => {
          const out = new URLSearchParams(prev)
          for (const [key, value] of Object.entries(next)) {
            if (value == null) out.delete(key)
            else out.set(key, value)
          }
          return out
        },
        { replace: true },
      ),
    [setParams],
  )

  return (
    <DashboardShell
      title="People & Team"
      kicker="Desk analytics"
      subtitle={
        view === 'person'
          ? name
            ? `${ROLE_LABEL[role]} · ${name}`
            : `Pick a ${ROLE_LABEL[role].toLowerCase()} to open their world`
          : 'Workload by stage, staffing, aging and leaderboards — built from every live deal and the team roster.'
      }
      tabs={<DashboardTabs tabs={TABS} active={view} onChange={(id) => patch({ view: id })} />}
      dataUpdatedAt={live.dataUpdatedAt}
      isFetching={live.isFetching}
      fill
    >
      {view === 'team' ? (
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto scrollbar-thin pr-0.5">
          <CohortBar cohort={cohort} onChange={setCohort} className="flex-shrink-0" />
          <TeamWorkloadPanel cohort={cohort} className="min-h-[340px] flex-shrink-0" />
          <div className="grid flex-shrink-0 grid-cols-1 gap-2 xl:grid-cols-2">
            <Leaderboard role="underwriter" cohort={cohort} className="h-[360px]" />
            <Leaderboard role="brokerage" cohort={cohort} title="Brokerages feeding the book" className="h-[360px]" />
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 gap-2">
          <PeoplePicker
            role={role}
            selected={name}
            onRole={(next) => patch({ role: next, name: null })}
            onSelect={(next) => patch({ name: next, view: 'person' })}
            className="w-[250px] flex-shrink-0"
          />
          <div className="min-h-0 min-w-0 flex-1 overflow-y-auto scrollbar-thin pr-0.5">
            {name ? (
              <PersonProfileView key={`${role}:${name}`} role={role} name={name} />
            ) : (
              <div className="flex h-full min-h-[240px] flex-col items-center justify-center rounded-lg border border-dashed border-amber-400/15 text-center">
                <UserRound size={20} className="mb-2 text-amber-300/60" aria-hidden="true" />
                <p className="font-display text-[20px] text-stone-200">Pick a {ROLE_LABEL[role].toLowerCase()}</p>
                <p className="mt-1 max-w-sm text-[11px] text-stone-500">
                  Their deals, relationships, aging and activity open here. Every figure opens the deals behind it.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </DashboardShell>
  )
}
